// Single source of truth for the Explorer's "n" metric and lifecycle labels.
//
// WP4 / Step 3a: n is defined ONE way everywhere it appears (the dropdown
// option and the header "n =" cell). It is the same y/x pair the sam-dbam
// undeploy check uses, delivered on the enriched-deployment payload:
//
//   named deployment (is_anonymous === false):  n = responding_count / sent_count  (y / x)
//   anonymous deployment (is_anonymous === true): n = completed_count / started_count, labeled
//
// Do NOT recompute n from responses locally anywhere else — read it from here.

/**
 * Resolve the numerator/denominator pair for n from an enriched deployment.
 * Missing counts default to 0.
 * @param {object} d enriched deployment ({ sent_count, responding_count,
 *   started_count, completed_count, is_anonymous })
 * @returns {{ numerator: number, denominator: number, anonymous: boolean }}
 */
export function computeN(d) {
    const sentCount = d?.sent_count || 0;
    const respondingCount = d?.responding_count || 0;
    const startedCount = d?.started_count || 0;
    const completedCount = d?.completed_count || 0;
    const anonymous = d?.is_anonymous ?? false;

    if (anonymous) {
        return { numerator: completedCount, denominator: startedCount, anonymous: true };
    }
    return { numerator: respondingCount, denominator: sentCount, anonymous: false };
}

/**
 * Bare "y/x" string, e.g. "3/5". Returns an em-dash pair when there is no
 * deployment.
 * @param {object} d enriched deployment
 * @returns {string}
 */
export function formatN(d) {
    if (!d) return '—/—';
    const { numerator, denominator } = computeN(d);
    return `${numerator}/${denominator}`;
}

/**
 * "y/x" for named deployments; "c/s (completed/started)" for anonymous ones so
 * the different denominator is never mistaken for the named y/x. Returns an
 * em-dash when there is no deployment.
 * @param {object} d enriched deployment
 * @returns {string}
 */
export function formatNLabel(d) {
    if (!d) return '—';
    const { anonymous } = computeN(d);
    return anonymous ? `${formatN(d)} (completed/started)` : formatN(d);
}

/**
 * Lifecycle sentence for the Explorer info bar, driven by the survey's
 * `survey_status` (delivered on every enriched-deployment row) and the
 * deployment's open state. One source so the header badge, the info-bar
 * message, and the empty-session explanation never disagree.
 *
 * Returns '' when no message is warranted:
 *   - no deployment selected
 *   - deployment closed (round-1 closed/history labeling owns that case)
 *   - survey `sent` with at least one response arriving (the live numbers ARE
 *     the display)
 *
 * @param {object} d enriched deployment
 * @returns {string}
 */
export function lifecycleMessage(d) {
    if (!d) return '';
    // Closed deployments are history — round 1 already labels them; no message.
    if (d.is_open === false) return '';

    const status = String(d.survey_status || '').toLowerCase();
    switch (status) {
        case 'deployed':
            return 'This survey has been deployed but not Staged or Sent.';
        case 'staged':
            return 'This survey has been staged but not yet Sent.';
        case 'sent': {
            // Waiting only while no response has landed yet. Once y > 0 the live
            // numbers speak for themselves, so we fall through to no message.
            const { numerator } = computeN(d);
            if (numerator === 0) {
                return `This survey has been Sent — waiting for recipients to respond (n = ${formatN(d)}).`;
            }
            return '';
        }
        default:
            return '';
    }
}
